'use client';

import type { ReactNode } from 'react';
import React from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Tooltip, type TooltipProps } from '@milanote/roma/components/base/tooltip/tooltip';
import { cx } from '@milanote/roma/utils/cx';
import { styles as buttonStyles } from './button';

const SEMANTIC_NAME = 'RomaUtilityButton';

const ICON_CLASS_NAME = 'size-5 shrink-0 transition-inherit-all';

export type UtilityButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface UtilityButtonProps extends Omit<AriaButtonProps, 'children'> {
    /** Icon-only content for the utility button. */
    children: ReactNode;
    /** Tooltip props displayed on hover or focus. */
    tooltip?: Omit<TooltipProps, 'children'>;
    /** The size variant of the button. */
    size?: UtilityButtonSize;
    /** Shows the selected state. */
    isSelected?: boolean;
}

/** Applies the standard utility button icon size while preserving custom icon classes. */
const renderUtilityIcon = (icon: ReactNode) => {
    if (!React.isValidElement<{ className?: string }>(icon)) return icon;

    return React.cloneElement(icon, {
        className: cx(ICON_CLASS_NAME, icon.props.className),
    });
};

export const UtilityButton = React.forwardRef<HTMLButtonElement, UtilityButtonProps>(
    (
        { children, className, tooltip, size = 'sm', isDisabled, isSelected, 'aria-label': ariaLabel, ...otherProps },
        ref,
    ) => {
        const button = (
            <AriaButton
                {...otherProps}
                ref={ref}
                aria-label={ariaLabel ?? (typeof tooltip?.title === 'string' ? tooltip.title : undefined)}
                data-icon-only
                data-selected={isSelected ? true : undefined}
                isDisabled={isDisabled}
                className={(state) =>
                    cx(
                        SEMANTIC_NAME,
                        buttonStyles.common.root,
                        buttonStyles.sizes[size].root,
                        buttonStyles.colors.tertiary.root,
                        'rounded-sm',
                        'text-fg-toolbar-primary hover:bg-primary_alpha hover:text-fg-toolbar-primary',
                        isSelected && 'bg-primary_alpha',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            >
                {renderUtilityIcon(children)}
            </AriaButton>
        );

        if (!tooltip) return button;

        return (
            <Tooltip {...tooltip} delay={tooltip.delay ?? 700}>
                {button}
            </Tooltip>
        );
    },
);

UtilityButton.displayName = SEMANTIC_NAME;
